import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faEnvelope } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import CardCheckbox from '@/components/ui/CardCheckbox';
import { faDiscord, faTelegram } from '@fortawesome/free-brands-svg-icons';
import type { Video } from '@/models';

export interface AutoPublishSettings {
  autoPublish: boolean;
  autoPublishSendDiscord: boolean;
  autoPublishSendMail: boolean;
  autoPublishSendTelegram: boolean;
}

interface AutoPublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: Video;
  onSave: (settings: AutoPublishSettings) => Promise<void>;
}

const AutoPublishModal: React.FC<AutoPublishModalProps> = ({
  isOpen,
  onClose,
  video,
  onSave
}) => {
  const [autoPublish, setAutoPublish] = useState(video.autoPublish);
  const [autoPublishSendDiscord, setAutoPublishSendDiscord] = useState(video.autoPublishSendDiscord);
  const [autoPublishSendTelegram, setAutoPublishSendTelegram] = useState(video.autoPublishSendTelegram);
  const [autoPublishSendMail, setAutoPublishSendMail] = useState(video.autoPublishSendMail);
  const [loading, setLoading] = useState(false);

  // Reset state when modal opens with new video data
  useEffect(() => {
    if (isOpen) {
      setAutoPublish(video.autoPublish);
      setAutoPublishSendDiscord(video.autoPublishSendDiscord);
      setAutoPublishSendTelegram(video.autoPublishSendTelegram);
      setAutoPublishSendMail(video.autoPublishSendMail);
    }
  }, [isOpen, video]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave({
        autoPublish,
        autoPublishSendDiscord,
        autoPublishSendMail,
        autoPublishSendTelegram
      });
      onClose();
    } catch {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Auto-Publish Einstellungen"
      icon={<FontAwesomeIcon icon={faRocket} className={`w-5 h-5 ${autoPublish ? 'text-yellow-600' : 'text-green-600'}`} />}
      maxWidth="lg"
      closeDisabled={loading}
    >
      <div className="p-6">
        <p className="text-gray-700 dark:text-gray-300 mb-6">
          Konfigurieren Sie die automatische Veröffentlichung für <span className="font-semibold">"{video.title}"</span>.
        </p>

        <div className="mb-6 space-y-4">
          {/* Auto-Publish Toggle */}
          <CardCheckbox
            checked={autoPublish}
            onChange={setAutoPublish}
            disabled={loading}
            icon={<FontAwesomeIcon icon={faRocket} className={`w-4 h-4 ${autoPublish ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'} mr-2`} />}
            label="Automatisch veröffentlichen"
            description="Wenn aktiviert, wird das Video nach Abschluss der Verarbeitung automatisch veröffentlicht."
          />

          {/* Notification Options - always visible, disabled when autoPublish is not checked */}
          <div className={`ml-6 space-y-3 ${!autoPublish ? 'opacity-50' : ''}`}>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Benachrichtigungen bei Veröffentlichung:
            </p>

            <CardCheckbox
              checked={autoPublishSendDiscord}
              onChange={setAutoPublishSendDiscord}
              disabled={loading || !autoPublish}
              icon={<FontAwesomeIcon icon={faDiscord} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />}
              label="Discord-Nachricht senden"
              description="Benachrichtigt alle Mitglieder in Discord über das neue Video"
            />

            <CardCheckbox
              checked={autoPublishSendTelegram}
              onChange={setAutoPublishSendTelegram}
              disabled={loading || !autoPublish}
              icon={<FontAwesomeIcon icon={faTelegram} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />}
              label="Telegram-Nachricht senden"
              description="Sendet eine Nachricht in den Telegram Channel"
            />

            <CardCheckbox
              checked={autoPublishSendMail}
              onChange={setAutoPublishSendMail}
              disabled={loading || !autoPublish}
              icon={<FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />}
              label="E-Mail-Benachrichtigung senden"
              description="Sendet eine E-Mail an alle Abonnenten mit aktivierten Benachrichtigungen"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Speichern...</span>
              </>
            ) : (
              <span>Speichern</span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AutoPublishModal;
